/**
 * Identity Service Server Client
 * A server-side client that behaves like Clerk's server client
 */

import { createIdentityServiceClient } from './client'
import type {
  UserCreate,
  UserUpdate,
  UserAbilitiesRequest,
  UsersQueryParams,
  UserRegisterRequest,
  OrganizationCreate,
  OrganizationUpdate,
  OrganizationsQueryParams,
} from './'

/**
 * Identity Service Server Client
 * Similar to Clerk's server client, but for the Identity Service
 */
export class IdentityServiceServerClient {
  private client: ReturnType<typeof createIdentityServiceClient>

  constructor(config: { serviceToken?: string; baseUrl?: string } = {}) {
    // Only pass serviceToken if it's defined
    const clientConfig: any = {}
    if (config.serviceToken) {
      clientConfig.serviceToken = config.serviceToken
    }
    if (config.baseUrl) {
      clientConfig.baseUrl = config.baseUrl
    }

    this.client = createIdentityServiceClient(clientConfig)
  }

  // ============================================================================
  // USER METHODS
  // ============================================================================

  /**
   * Get a paginated list of users with optional filtering
   */
  async getUsers(params?: UsersQueryParams) {
    const response = await this.client.GET('/users', {
      params: { query: params },
    })

    if (response.error) {
      throw new Error(`Failed to fetch users: ${response.error}`)
    }

    return response.data
  }

  /**
   * Get a single user by ID
   */
  async getUser(userId: string) {
    const response = await this.client.GET('/users/{id}', {
      params: { path: { id: userId } },
    })

    if (response.error) {
      throw new Error(`Failed to fetch user: ${response.error}`)
    }

    return response.data
  }

  /**
   * Get user abilities for a specific subject and action
   */
  async getUserAbilities(params: UserAbilitiesRequest) {
    const response = await this.client.GET('/users/me/abilities', {
      params: { query: params },
    })

    if (response.error) {
      throw new Error(`Failed to fetch user abilities: ${response.error}`)
    }

    return response.data
  }

  /**
   * Create a new user
   */
  async createUser(userData: UserCreate) {
    const response = await this.client.POST('/users', {
      body: userData,
    })

    if (response.error) {
      throw new Error(`Failed to create user: ${response.error}`)
    }

    return response.data
  }

  /**
   * Update an existing user
   */
  async updateUser(userId: string, userData: UserUpdate) {
    const response = await this.client.PUT('/users/{id}', {
      params: { path: { id: userId } },
      body: userData,
    })

    if (response.error) {
      throw new Error(`Failed to update user: ${response.error}`)
    }

    return response.data
  }

  /**
   * Delete a user
   */
  async deleteUser(userId: string) {
    const response = await this.client.DELETE('/users/{id}', {
      params: { path: { id: userId } },
    })

    if (response.error) {
      throw new Error(`Failed to delete user: ${response.error}`)
    }

    return response.data
  }

  /**
   * Register a new user
   */
  async registerUser(request: UserRegisterRequest) {
    const response = await this.client.POST('/users/register', {
      body: request,
    })

    if (response.error) {
      throw new Error(`Failed to register user: ${response.error}`)
    }

    return response.data
  }

  // ============================================================================
  // ORGANIZATION METHODS
  // ============================================================================

  /**
   * Get a paginated list of organizations with optional filtering
   */
  async getOrganizations(params?: OrganizationsQueryParams) {
    const response = await this.client.GET('/organizations', {
      params: { query: params },
    })

    if (response.error) {
      throw new Error(`Failed to fetch organizations: ${response.error}`)
    }

    return response.data
  }

  /**
   * Get a single organization by ID
   */
  async getOrganization(organizationId: string) {
    const response = await this.client.GET('/organizations/{id}', {
      params: { path: { id: organizationId } },
    })

    if (response.error) {
      throw new Error(`Failed to fetch organization: ${response.error}`)
    }

    return response.data
  }

  /**
   * Create a new organization
   */
  async createOrganization(organizationData: OrganizationCreate) {
    const response = await this.client.POST('/organizations', {
      body: organizationData,
    })

    if (response.error) {
      throw new Error(`Failed to create organization: ${response.error}`)
    }

    return response.data
  }

  /**
   * Update an existing organization
   */
  async updateOrganization(organizationId: string, organizationData: OrganizationUpdate) {
    const response = await this.client.PUT('/organizations/{id}', {
      params: { path: { id: organizationId } },
      body: organizationData,
    })

    if (response.error) {
      throw new Error(`Failed to update organization: ${response.error}`)
    }

    return response.data
  }

  /**
   * Delete an organization
   */
  async deleteOrganization(organizationId: string) {
    const response = await this.client.DELETE('/organizations/{id}', {
      params: { path: { id: organizationId } },
    })

    if (response.error) {
      throw new Error(`Failed to delete organization: ${response.error}`)
    }

    return response.data
  }

  // ============================================================================
  // USER PROFILE METHODS
  // ============================================================================

  /**
   * Get the current user's profile
   */
  async getUserProfile() {
    const response = await this.client.GET('/users/profile')

    if (response.error) {
      throw new Error(`Failed to fetch user profile: ${response.error}`)
    }

    return response.data
  }
}

/**
 * Create an Identity Service server client
 * Similar to Clerk's createClerkClient()
 */
export function createIdentityServiceServerClient(config: { serviceToken?: string; baseUrl?: string } = {}) {
  return new IdentityServiceServerClient(config)
}

// Export types
export type {
  UserCreate,
  UserUpdate,
  UserAbilitiesRequest,
  UsersQueryParams,
  UserRegisterRequest,
  OrganizationCreate,
  OrganizationUpdate,
  OrganizationsQueryParams,
} from './'
